import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getAdminCostSummary } from '../../services/api';

export default function AdminCost() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminCostSummary()
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <p className="text-gray-500 py-12 text-center">Loading cost data...</p>;
  }

  const totals = data?.totals || {};
  const costUsd = parseFloat(totals.estimated_cost_usd) || 0;
  const chartData = (data?.dailyTrend || []).map((d) => ({
    day: new Date(d.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    cost: parseFloat(d.cost_usd) || 0,
    tokens: parseInt(d.total_tokens, 10) || 0,
  }));

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-1">AI Cost Analysis</h2>
      <p className="text-gray-500 mb-6">Anthropic token usage per audit (Claude + Vision OCR)</p>

      {!data?.trackingEnabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6 text-sm text-yellow-800">
          {data?.message || 'Run `npm run db:supabase` to apply the audit_ai_usage migration. Token tracking will populate after the next deploy instruments Claude calls.'}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Audits tracked" value={totals.audit_count || 0} />
        <StatCard label="Input tokens" value={Number(totals.input_tokens || 0).toLocaleString()} />
        <StatCard label="Output tokens" value={Number(totals.output_tokens || 0).toLocaleString()} />
        <StatCard label="Est. cost (USD)" value={`$${costUsd.toFixed(2)}`} color="text-teal-600" />
      </div>

      {chartData.length > 0 && (
        <div className="glass-card rounded-2xl border border-teal-100/50 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Daily cost (30 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v) => [`$${Number(v).toFixed(4)}`, 'Cost']} />
              <Bar dataKey="cost" fill="#0d9488" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="glass-card rounded-2xl border border-teal-100/50 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Cost by agency</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-teal-50/50 text-left text-gray-500">
              <th className="px-4 py-3">Agency</th>
              <th className="px-4 py-3 text-center">Audits</th>
              <th className="px-4 py-3 text-right">Input tokens</th>
              <th className="px-4 py-3 text-right">Output tokens</th>
              <th className="px-4 py-3 text-right">Est. cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(data?.byAgency || []).length === 0 ? (
              <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-400">No usage recorded yet</td></tr>
            ) : data.byAgency.map((row) => (
              <tr key={row.agency_id} className="hover:bg-teal-50/30">
                <td className="px-4 py-3 font-medium">{row.agency_name}</td>
                <td className="px-4 py-3 text-center">{row.audit_count}</td>
                <td className="px-4 py-3 text-right">{Number(row.input_tokens).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">{Number(row.output_tokens).toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-semibold text-teal-700">
                  ${parseFloat(row.estimated_cost_usd || 0).toFixed(4)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, color = 'text-gray-800' }) {
  return (
    <div className="glass-card rounded-2xl border border-teal-100/50 p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  );
}
